import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterLink } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';

@Component({
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, RouterLink],
  template: `
    <div class="w-full max-w-7xl items-center" role="main">
      <section class="mb-8 flex lg:mb-0" aria-label="Authentication status">
        <div
          class="flex min-w-[400px] flex-col items-center rounded-xl bg-white p-8 text-black shadow-lg md:p-10"
        >
          <!-- Comcast Business Logo -->
          <div class="mb-4 flex md:justify-center">
            <a routerLink="/" class="inline-block" aria-label="Go to homepage">
              <img
                src="assets/images/logos/CB_Logo_Color_RGB.svg"
                alt="Comcast Business"
                class="h-10 w-auto md:h-14"
              />
            </a>
          </div>

          <div
            aria-live="polite"
            aria-atomic="true"
            class="w-full min-w-[500px] text-center"
          >
            @if (!error) {
              <h1
                class="my-8 text-center font-montserrat text-2xl font-bold text-black"
              >
                Signing you in...
              </h1>
              <div
                class="my-6 flex justify-center"
                role="status"
                aria-label="Loading, please wait"
              >
                <mat-spinner [diameter]="75"></mat-spinner>
              </div>
            } @else {
              <h1 #errorHeading tabindex="-1" class="mb-2 mt-4 text-xl">
                We're having trouble signing you in
              </h1>
              <p class="text-center text-gray-600">
                Something went wrong during authentication. Please try again or
                contact us for assistance.
              </p>
              <p class="my-8 text-center text-lg font-semibold text-black">
                <a href="tel:18668920915" class="hover:underline">
                  1-866-892-0915
                </a>
              </p>
              <button type="button" class="btn-primary" (click)="retry()">
                Return to login
              </button>
            }
          </div>
        </div>
      </section>
    </div>
  `,

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OktaCallbackCustomComponent implements OnInit {
  error: string | undefined;

  @ViewChild('errorHeading') errorHeading:
    | ElementRef<HTMLHeadingElement>
    | undefined;

  private oktaAuth: OktaAuth = inject(OKTA_AUTH);

  private router = inject(Router);

  private cdr = inject(ChangeDetectorRef);

  async ngOnInit(): Promise<void> {
    try {
      const isAuthenticated = await this.oktaAuth.isAuthenticated();
      if (isAuthenticated) {
        this.router.navigate(['/']);
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const hasCallback = params.has('code') || params.has('error');
      if (!hasCallback) {
        this.oktaAuth.signInWithRedirect();
        return;
      }
      await this.oktaAuth.handleLoginRedirect();
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'An error occurred during sign-in.';
      this.error = message;
      this.cdr.markForCheck();
      setTimeout(() => this.errorHeading?.nativeElement.focus());
    }
  }

  retry(): void {
    this.oktaAuth.signInWithRedirect();
  }
}
